import { Configuration } from '../../generated-src/client/account/configuration';
import { AuthApi } from '../../generated-src/client/account/api';
import { AccountApi } from '../../generated-src/client/account/api';

import { InterviewsApi } from '../../generated-src/client/interviews/api';
import { PositionsApi } from '../../generated-src/client/positions/api';
import { QuestionsApi } from '../../generated-src/client/interviews/api';
import { TeamsApi } from '../../generated-src/client/teams/api';
import { StatisticsApi } from '../../generated-src/client/statistics/api';
import { GenerationApi } from '../../generated-src/client/generation/api';
import { InterviewSettingsApi } from '../../generated-src/client/interview-settings/api';
import { BrandingApi } from '../../generated-src/client/branding/api';
import { CompaniesApi } from '../../generated-src/client/companies/api';
import { CompanyCreditsApi } from '../../generated-src/client/company-credits/api';
import { ReportsApi } from '../../generated-src/client/reports/api';
import { ComplaintsApi } from '../../generated-src/client/complaints/api';
import {
    AccountTelegramApi,
    AccountNotificationPreferencesApi,
} from '../../generated-src/client/account-notifications/api';
// Professional: Always import the auth store for token access
import { useAuthStore } from '../store/authStore';
import axios from 'axios';
import { shouldRedirectToLoginOnApiError } from './authErrorHandling';

/**
 * Интерфейс API клиента с методами для всех доменов системы
 * Предоставляет типизированный доступ к API эндпоинтам
 */
export interface ApiClient {
    /** API для аутентификации */
    auth: AuthApi;
    /** API для управления аккаунтами */
    account: AccountApi;
    /** API для работы с интервью */
    interviews: InterviewsApi;
    /** API для работы с вакансиями */
    positions: PositionsApi;
    /** API для работы с вопросами */
    questions: QuestionsApi;
    /** API для управления командами */
    teams: TeamsApi;
    /** API для статистики */
    statistics: StatisticsApi;
    /** API для генерации контента */
    generation: GenerationApi;
    /** API для настроек интервью */
    interviewSettings: InterviewSettingsApi;
    /** API для брендинга */
    branding: BrandingApi;
    /** API для управления компаниями */
    companies: CompaniesApi;
    /** API для кредитов компании */
    companyCredits: CompanyCreditsApi;
    /** API для отчетов */
    reports: ReportsApi;
    /** API для жалоб */
    complaints: ComplaintsApi;
    /** API для привязки Telegram-бота нотификаций (TASKNEIROKLYUCH-647) */
    accountTelegram: AccountTelegramApi;
    /** API для настроек нотификаций (TASKNEIROKLYUCH-647) */
    accountNotificationPreferences: AccountNotificationPreferencesApi;
}

/**
 * Вспомогательная функция для получения очищенного базового пути API
 * @returns {string} Очищенный базовый путь к API
 */
function getApiBase() {
    const raw = (process.env.REACT_APP_API_BASE_URL || '/api/v1').toString();
    const base = raw.trim().replace(/\/+$/, '');
    return base;
}
function getApiPath() {
    try {
        const base = getApiBase();
        // If absolute, extract pathname; else take as-is
        if (/^https?:\/\//i.test(base)) {
            return new URL(base).pathname.replace(/\/+$/, '');
        }
        return base.replace(/\/+$/, '');
    } catch {
        return '/api';
    }
}

// Inject Authorization header for all requests to our backend API
axios.interceptors.request.use((config) => {
    const token = useAuthStore.getState().token;
    console.log('🔍 Axios interceptor: Processing request:', {
        url: config.url,
        baseURL: config.baseURL,
        method: config.method,
        hasToken: !!token,
        tokenLength: token?.length || 0
    });
    
    if (!token) {
        console.log('🔍 Axios interceptor: No token found');
        return config;
    }

    const apiBase = getApiBase();
    const apiPath = getApiPath();

    const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
    const url = fullUrl.toString();

    const isAbsolute = /^https?:\/\//i.test(url);
    const matchesApiBase = isAbsolute ? url.startsWith(apiBase) : false;
    const matchesApiPath = !isAbsolute && (url.startsWith(apiPath + '/') || url === apiPath);

    console.log('🔍 Axios interceptor: URL analysis:', {
        url,
        isAbsolute,
        matchesApiBase,
        matchesApiPath,
        apiBase,
        apiPath
    });

    // Добавляем токен для всех API запросов
    if (matchesApiBase || matchesApiPath || url.includes('/api/')) {
        config.headers = config.headers || {} as any;
        if (!('Authorization' in config.headers)) {
            (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
            console.log('🔍 Axios interceptor: Added Authorization header for URL:', url);
        } else {
            console.log('🔍 Axios interceptor: Authorization header already exists for URL:', url);
        }
    } else {
        console.log('🔍 Axios interceptor: URL does not match API patterns:', url);
    }

    return config;
});

/**
 * Professional API client factory.
 * - Always injects the current JWT token from zustand store into all requests via OpenAPI Configuration's accessToken property.
 * - Removes all Basic Auth logic (username/password).
 * - Ensures Authorization: Bearer <token> is set for all protected endpoints.
 * - If token is null, requests are sent without Authorization (for public endpoints).
 * - To update token after login/logout, simply re-create the client (or use a singleton pattern if desired).
 * 
 * @param basePath - Базовый путь к API (по умолчанию из переменных окружения)
 * @returns {ApiClient} Настроенный API клиент с аутентификацией
 * 
 * @example
 * ```typescript
 * const client = createApiClient();
 * const positions = await client.positions.getPositions();
 * ```
 */
export function createApiClient(
    basePath: string = process.env.REACT_APP_API_BASE_URL || '/api/v1'
): ApiClient {
    const sanitizedBasePath = (basePath || '')
        .toString()
        .trim()
        .replace(/\/+$/, '');
    // Always provide a function to access the latest token from zustand
    const config = new Configuration({
        basePath: sanitizedBasePath,
        accessToken: async () => {
            const token = useAuthStore.getState().token;
            return token || '';
        },
        // baseOptions can be extended here if needed
    });

    return {
        auth: new AuthApi(config),
        account: new AccountApi(config),
        interviews: new InterviewsApi(config),
        positions: new PositionsApi(config),
        questions: new QuestionsApi(config),
        teams: new TeamsApi(config),
        statistics: new StatisticsApi(config),
        generation: new GenerationApi(config),
        interviewSettings: new InterviewSettingsApi(config),
        branding: new BrandingApi(config),
        companies: new CompaniesApi(config),
        companyCredits: new CompanyCreditsApi(config),
        reports: new ReportsApi(config),
        complaints: new ComplaintsApi(config),
        accountTelegram: new AccountTelegramApi(config),
        accountNotificationPreferences: new AccountNotificationPreferencesApi(config),
    };
}

// Global axios interceptor for authentication errors
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (shouldRedirectToLoginOnApiError(error, getApiBase())) {
            useAuthStore.getState().logout();
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

/**
 * Global API client instance WITHOUT authentication.
 * Use this for public endpoints that don't require authorization.
 * For protected endpoints, use apiService.getAuthenticatedClient() instead.
 */
export const publicApiClient = createApiClient();
